import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import { buildDaySpine, shiftDayKey, formatDaySwitcherLabel, SPINE_MARKER } from "../utils/daySpine";

// #2140 (docs/score/announcer-ui-redesign.md §4.2) — band 2, "the day": a
// non-scrolling vertical spine of the selected ring's CLASSES, one venue-local
// day at a time. Consumes classes/rides/breaks/liveClassId/summaryByClassId/
// isClassPlacedFromSummary straight off the EXISTING RingDataProvider poll set
// (useRingData()) — this component adds NO new hook and opens NO new poll
// (docs/score/announcer-ui-redesign.md §11, #2137's whole point).

// Fixed pixel heights for header/return-control/footer (never vary with
// content) and the two row densities the compress ladder switches between.
// AnnouncerFrame's own `frame-band-day` wrapper is `overflow:hidden` by
// contract (#2138) — this component nests its OWN scroller (the rows
// container below) rather than touching that.
const HEADER_HEIGHT = 40;
const RETURN_CONTROL_HEIGHT = 28;
const FOOTER_HEIGHT = 28;
const ROW_HEIGHT_NORMAL = 40;
const ROW_HEIGHT_COMPRESSED = 24;

function MarkerDot({ marker }) {
  const base = { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 };
  if (marker === SPINE_MARKER.DONE) {
    return <Box data-testid="day-spine-marker-dot" sx={{ ...base, bgcolor: "text.primary" }} />;
  }
  if (marker === SPINE_MARKER.RUNNING) {
    return (
      <Box
        data-testid="day-spine-marker-dot"
        sx={{
          ...base,
          bgcolor: colors.foxOrange,
          boxShadow: `0 0 0 3px rgba(248, 153, 29, 0.35)`,
        }}
      />
    );
  }
  if (marker === SPINE_MARKER.BREAK) {
    return (
      <Box
        data-testid="day-spine-marker-dot"
        sx={{ ...base, border: "1px dashed", borderColor: "text.secondary" }}
      />
    );
  }
  // TO_COME — hollow dot.
  return (
    <Box
      data-testid="day-spine-marker-dot"
      sx={{ ...base, border: "1.5px solid", borderColor: "text.secondary" }}
    />
  );
}

function ClassRow({ entry, isViewing, density, onSelect, rowRef, t }) {
  const rowHeight = density === "compressed" ? ROW_HEIGHT_COMPRESSED : ROW_HEIGHT_NORMAL;
  // Doc §11 — "when [RUNNING and VIEWING] sit on the same row, only RUNNING
  // is drawn plus the row's selected outline": the outline always follows
  // VIEWING, but the separate "Viewing" marker/label only renders when it is
  // NOT also the running row (entry.isRunning), so the two states never
  // double up on one row.
  return (
    <Box
      ref={rowRef}
      data-testid={`day-spine-row-${entry.classId}`}
      data-marker={entry.marker}
      data-running={entry.isRunning ? "true" : "false"}
      data-viewing={isViewing ? "true" : "false"}
      onClick={onSelect}
      sx={{
        height: rowHeight,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        cursor: "pointer",
        outline: isViewing ? `2px solid ${colors.foxOrange}` : "2px solid transparent",
        outlineOffset: "-2px",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <MarkerDot marker={entry.marker} />
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant={density === "compressed" ? "caption" : "body2"}
          noWrap
          sx={{ fontWeight: 600, lineHeight: 1.2 }}
        >
          {entry.classNumber} {entry.className}
        </Typography>
        {/* Compressed density drops the status line entirely rather than
            tightening its type, a deliberate choice toward the legibility
            floor (doc: "Rows compress toward a legibility floor"), not an
            oversight — a squeezed second line reads worse than no second
            line at the sizes this ladder targets. */}
        {density !== "compressed" && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.2 }}>
            {entry.statusLine}
          </Typography>
        )}
      </Stack>
      {isViewing && !entry.isRunning && (
        <Typography
          data-testid="day-spine-viewing-marker"
          variant="caption"
          noWrap
          sx={{ color: colors.foxOrange, fontWeight: 700, flexShrink: 0 }}
        >
          {t("daySpine.viewing")}
        </Typography>
      )}
    </Box>
  );
}

function BreakRow({ entry, density }) {
  const rowHeight = density === "compressed" ? ROW_HEIGHT_COMPRESSED : ROW_HEIGHT_NORMAL;
  return (
    <Box
      data-testid={`day-spine-break-${entry.id}`}
      data-marker={entry.marker}
      sx={{
        height: rowHeight,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
      }}
    >
      <MarkerDot marker={entry.marker} />
      <Typography
        variant={density === "compressed" ? "caption" : "body2"}
        noWrap
        color="text.secondary"
        sx={{ flex: 1, lineHeight: 1.2 }}
      >
        {entry.label}
      </Typography>
      {entry.statusLine && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {entry.statusLine}
        </Typography>
      )}
    </Box>
  );
}

export default function DaySpine() {
  const { t } = useTranslation("announcer");
  const { selectedRing, viewingClassId, setViewingClassId } = useAnnouncerContext();
  const { classes, rides, breaks, statuses, liveClassId, isClassPlacedFromSummary, todayKey } =
    useRingData();

  // Band-2-owned (doc §4.2) — a LAZY initializer capturing todayKey exactly
  // ONCE at mount, never recomputed from a ticking clock (AC10: the shown
  // day must never auto-advance on its own, including at a real midnight
  // rollover mid-session). Reset only on an explicit ring switch below.
  const [shownDayKey, setShownDayKey] = useState(() => todayKey);
  const previousRingRef = useRef(selectedRing);
  useEffect(() => {
    if (previousRingRef.current === selectedRing) return;
    previousRingRef.current = selectedRing;
    // A ring switch is a discrete event, not a tick — reading todayKey here
    // (rather than adding it to the dep array) is a one-shot "now", the same
    // idiom AnnouncerContext's own viewingClassId reset uses.
    setShownDayKey(todayKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRing]);

  const spine = useMemo(
    () =>
      buildDaySpine({
        rides,
        breaks,
        classes,
        statuses,
        liveClassId,
        isClassPlacedFromSummary,
        dayKey: shownDayKey,
        todayKey,
      }),
    [rides, breaks, classes, statuses, liveClassId, isClassPlacedFromSummary, shownDayKey, todayKey],
  );

  // VIEWING is null ("following the ring") until an explicit click pins it —
  // doc §11: "they usually sit on the same row" is exactly this default.
  const effectiveViewingClassId = viewingClassId ?? liveClassId;

  // Compress-then-scroll ladder (AC4/AC14/AC15/AC16). Measures the FIXED
  // rows container itself (never the content — that would be a CSS reflow
  // feedback loop), then picks density/scroll off the entry count against
  // that measured height. Starts at density "normal" before the first
  // measurement lands so a fixture that fits never flashes compressed.
  const rowsContainerRef = useRef(null);
  const [rowsAreaHeight, setRowsAreaHeight] = useState(null);
  useEffect(() => {
    const el = rowsContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) setRowsAreaHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rowCount = spine.entries.length;
  const fitsNormal = rowsAreaHeight == null || rowCount * ROW_HEIGHT_NORMAL <= rowsAreaHeight;
  const fitsCompressed = rowsAreaHeight == null || rowCount * ROW_HEIGHT_COMPRESSED <= rowsAreaHeight;
  const density = fitsNormal ? "normal" : "compressed";
  const scrollEnabled = !fitsNormal && !fitsCompressed;

  // #2140 AC15/AC16 — this tracks whether the RUNNING row is out of view so
  // an edge chip can offer to jump to it, but it may ONLY ever call
  // setState. It never calls scrollIntoView/sets scrollTop itself — only
  // goToRunning (the chip's own onClick, below) does that, so the band never
  // moves on its own when RUNNING advances.
  const [runningRowEl, setRunningRowEl] = useState(null);
  const [runningOffscreen, setRunningOffscreen] = useState(false);
  useEffect(() => {
    if (
      !scrollEnabled ||
      !runningRowEl ||
      !rowsContainerRef.current ||
      typeof IntersectionObserver === "undefined"
    ) {
      setRunningOffscreen(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setRunningOffscreen(!entry.isIntersecting),
      { root: rowsContainerRef.current, threshold: 0 },
    );
    observer.observe(runningRowEl);
    return () => observer.disconnect();
  }, [scrollEnabled, runningRowEl]);

  const goToRunning = useCallback(() => {
    runningRowEl?.scrollIntoView({ block: "nearest" });
  }, [runningRowEl]);

  const showReturnControl = shownDayKey !== todayKey;

  return (
    <Box data-testid="day-spine" sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        data-testid="day-spine-header"
        sx={{
          height: HEADER_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <IconButton
          data-testid="day-spine-prev-day"
          size="small"
          aria-label={t("daySpine.prevDay")}
          onClick={() => setShownDayKey((k) => shiftDayKey(k, -1))}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography data-testid="day-spine-day-label" variant="subtitle2" noWrap>
          {formatDaySwitcherLabel(shownDayKey)}
        </Typography>
        <IconButton
          data-testid="day-spine-next-day"
          size="small"
          aria-label={t("daySpine.nextDay")}
          onClick={() => setShownDayKey((k) => shiftDayKey(k, 1))}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* AC11/AC12 — this slot is ALWAYS this height, shown or not, so the
          rows below it never shift depending on whether it has content. */}
      <Box
        data-testid="day-spine-return-control"
        data-visible={showReturnControl ? "true" : "false"}
        sx={{ height: RETURN_CONTROL_HEIGHT, flexShrink: 0, display: "flex", alignItems: "center", px: 1 }}
      >
        {showReturnControl && (
          <Typography
            data-testid="day-spine-return-control-label"
            component="button"
            type="button"
            onClick={() => setShownDayKey(todayKey)}
            variant="caption"
            noWrap
            sx={{
              cursor: "pointer",
              color: colors.foxOrange,
              background: "none",
              border: "none",
              p: 0,
              textAlign: "left",
              font: "inherit",
              width: "100%",
            }}
          >
            {t("daySpine.returnControl")}
          </Typography>
        )}
      </Box>

      <Box
        ref={rowsContainerRef}
        data-testid="day-spine-rows"
        data-density={density}
        data-scroll-enabled={scrollEnabled ? "true" : "false"}
        sx={{ flex: 1, minHeight: 0, overflowY: scrollEnabled ? "auto" : "hidden" }}
      >
        {spine.entries.map((entry) => {
          if (entry.kind === "BREAK") {
            return <BreakRow key={entry.id} entry={entry} density={density} />;
          }
          const isViewing = entry.classId === effectiveViewingClassId;
          return (
            <ClassRow
              key={entry.id}
              entry={entry}
              isViewing={isViewing}
              density={density}
              t={t}
              onSelect={() => setViewingClassId(entry.classId)}
              rowRef={entry.isRunning ? setRunningRowEl : undefined}
            />
          );
        })}
      </Box>

      {scrollEnabled && runningOffscreen && (
        <Chip
          data-testid="day-spine-offscreen-chip"
          label={t("daySpine.jumpToRunning")}
          size="small"
          onClick={goToRunning}
          sx={{
            position: "absolute",
            right: 8,
            bottom: FOOTER_HEIGHT + 8,
            bgcolor: colors.foxOrange,
            color: "#fff",
            cursor: "pointer",
            zIndex: 1,
          }}
        />
      )}

      <Box
        data-testid="day-spine-footer"
        sx={{
          height: FOOTER_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          px: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {t("daySpine.footer", spine.footer)}
        </Typography>
      </Box>
    </Box>
  );
}
